import { useEffect, useRef } from "react";
import { isEmpty, isEqual } from "lodash";
import { IFRAME_ID } from "./constants";

interface DynamicContent {
  bodyEnd?: string;
  bodyStart?: string;
  headEnd?: string;
  headStart?: string;
}

interface InjectedElements {
  bodyEnd: Set<Element>;
  bodyStart: Set<Element>;
  headEnd: Set<Element>;
  headStart: Set<Element>;
}

const DYNAMIC_CONTENT_DATA_ATTRIBUTE = "data-dynamic-content-source";

/**
 * Hook that dynamically injects content into iframe head and body sections
 * without causing iframe reloads. Manages content lifecycle with proper cleanup.
 */
export const useDynamicContentInjection = (content: DynamicContent) => {
  const previousContentRef = useRef<DynamicContent>({});
  const injectedElementsRef = useRef<InjectedElements>({
    headStart: new Set(),
    headEnd: new Set(),
    bodyStart: new Set(),
    bodyEnd: new Set(),
  });

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    let isCleanedUp = false;

    const createElementsFromHTML = (htmlString: string): Element[] => {
      if (isEmpty(htmlString?.trim())) return [];

      const tempContainer = document.createElement("div");
      tempContainer.innerHTML = htmlString.trim();
      return Array.from(tempContainer.children);
    };

    const removePreviousElements = (section: keyof InjectedElements) => {
      const injectedElements = injectedElementsRef.current;
      const elementSet = injectedElements[section];
      for (const element of elementSet) {
        element.remove();
      }
      elementSet.clear();
    };

    const injectElements = (
      elements: Element[],
      target: HTMLElement,
      position: "start" | "end",
      section: keyof InjectedElements,
    ) => {
      const injectedElements = injectedElementsRef.current;

      for (const element of elements) {
        element.setAttribute(DYNAMIC_CONTENT_DATA_ATTRIBUTE, section);

        const clonedElement = element.cloneNode(true) as Element;
        clonedElement.setAttribute(DYNAMIC_CONTENT_DATA_ATTRIBUTE, section);

        if (position === "start") {
          target.insertBefore(clonedElement, target.firstChild);
        } else {
          target.appendChild(clonedElement);
        }

        injectedElements[section].add(clonedElement);
      }
    };

    const processContentSection = (
      currentHTML: string | undefined,
      previousHTML: string | undefined,
      section: keyof InjectedElements,
      target: HTMLElement,
      position: "start" | "end",
    ) => {
      if (isEqual(currentHTML, previousHTML)) return;

      removePreviousElements(section);

      if (currentHTML && !isEmpty(currentHTML.trim())) {
        const elements = createElementsFromHTML(currentHTML);
        if (!isEmpty(elements)) {
          injectElements(elements, target, position, section);
        }
      }
    };

    const performInjection = () => {
      if (isCleanedUp) return false;

      const iframe = document.getElementById(IFRAME_ID) as HTMLIFrameElement;
      if (!iframe) return false;

      const iframeDocument = iframe.contentDocument;
      if (!iframeDocument?.head || !iframeDocument?.body) return false;

      const previousContent = previousContentRef.current;
      const contentSections = [
        {
          current: content.headStart,
          previous: previousContent.headStart,
          section: "headStart" as const,
          target: iframeDocument.head,
          position: "start" as const,
        },
        {
          current: content.headEnd,
          previous: previousContent.headEnd,
          section: "headEnd" as const,
          target: iframeDocument.head,
          position: "end" as const,
        },
        {
          current: content.bodyStart,
          previous: previousContent.bodyStart,
          section: "bodyStart" as const,
          target: iframeDocument.body,
          position: "start" as const,
        },
        {
          current: content.bodyEnd,
          previous: previousContent.bodyEnd,
          section: "bodyEnd" as const,
          target: iframeDocument.body,
          position: "end" as const,
        },
      ];

      for (const {
        current,
        previous,
        section,
        target,
        position,
      } of contentSections) {
        processContentSection(current, previous, section, target, position);
      }

      previousContentRef.current = { ...content };
      return true;
    };

    const startRetryMechanism = () => {
      const maxAttempts = 50;
      const attemptInterval = 100;
      let attempts = 0;

      const attemptInjection = () => {
        if (isCleanedUp) return;

        if (performInjection()) {
          clearInterval(intervalId);
          return;
        }

        attempts++;
        if (attempts >= maxAttempts) {
          clearInterval(intervalId);
        }
      };

      attemptInjection();

      if (!isCleanedUp) {
        intervalId = setInterval(attemptInjection, attemptInterval);
      }
    };

    const timeoutId = setTimeout(() => {
      if (!isCleanedUp) {
        startRetryMechanism();
      }
    }, 50);

    return () => {
      isCleanedUp = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [content]);

  const cleanup = () => {
    const injectedElements = injectedElementsRef.current;

    const elementSets: Set<Element>[] = [
      injectedElements.headStart,
      injectedElements.headEnd,
      injectedElements.bodyStart,
      injectedElements.bodyEnd,
    ];

    for (const elementSet of elementSets) {
      for (const element of elementSet) {
        element.remove();
      }
      elementSet.clear();
    }

    previousContentRef.current = {};
  };

  return { cleanup };
};
